import { TMaintenanceTicketStatus, TRESPONSE_GET_ALL_MAINTENANCE_TICKETS } from '@monorepo/types';
import { TAccordionActionInfo, TAccordionTableVM } from '../../model/accordion-table.types';
import { TICKET_LIST_HELPER } from './ticket-list-helper';
import { ACCORDION_TABLE_MODIFIER } from '../../model/accordion-table.modifier';

// NOTE[Ramya]: add actions based on feature codes after v1

const TICKET_ACTIONS_CONF: {
  actions: { status: TMaintenanceTicketStatus; actionIds: string[] }[];
} = {
  actions: [
    {
      status: 'MAINTENANCE_PENDING',
      actionIds: ['START_TASK', 'RE_ASSIGN_TICKET'],
    },
    {
      status: 'IN_MAINTENANCE',
      actionIds: ['EDIT_CHECKLIST', 'COMPLETE_TASK', 'RAISE_REQUEST', 'VIEW_REQUEST_DETAILS'],
    },
    {
      status: 'MAINTENANCE_COMPLETED',
      actionIds: ['VIEW_REQUEST_DETAILS'],
    },
    {
      status: 'MAINTENANCE_OVERDUE',
      actionIds: ['START_TASK', 'RE_ASSIGN_TICKET'],
    },
  ],
};

function _getactionsInfoByStatus(selectedTicketStatus: TMaintenanceTicketStatus, actionConf: TAccordionActionInfo[]) {
  const actionsFilteredByStatus = TICKET_ACTIONS_CONF.actions.find((action) => action.status === selectedTicketStatus);
  if (actionsFilteredByStatus) {
    const actionsInfoByStatus = actionConf.filter((actionInfo) =>
      actionsFilteredByStatus.actionIds.includes(actionInfo.actionId as string),
    );
    return actionsInfoByStatus;
  } else {
    return [];
  }
}

export const MAINTENANCE_TICKETS_ACCORDION_TABLE_MODEL = {
  createModel(
    tickets: TRESPONSE_GET_ALL_MAINTENANCE_TICKETS['maintenanceTickets'],
    actionConf: TAccordionActionInfo[],
    selectedTicketStatus?: TMaintenanceTicketStatus,
    redirectedTicketId?: number,
  ): TAccordionTableVM {
    return ACCORDION_TABLE_MODIFIER.createModel(
      [
        { label: 'tm_container_labels.ScheduledDate', colSize: 7 },
        { label: 'tm_container_labels.MachineSerialIDheader', colSize: 9 },
        { label: 'tm_container_labels.LocationHeader', colSize: 7 },
      ],
      tickets.map((ticket) => {
        return {
          id: ticket.ticketInfo.ticketId,
          tag: undefined,
          isMinimized: redirectedTicketId ? !(redirectedTicketId === ticket.ticketInfo.ticketId) : true,
          rowItems: [
            {
              type: 'TEXT',
              value: ticket.ticketInfo.dueAt,
            },
            { type: 'TEXT', value: ticket.machineDescription.factorySerialNumber },
            { type: 'TEXT', value: ticket.ticketInfo.locationInfo.locationDisplayCode },
          ],
          moreInfo: [
            {
              items: [
                {
                  size: 12,
                  label: 'tm_container_labels.MachineSerialID',
                  values: TICKET_LIST_HELPER.mapToValuesList([ticket.machineDescription.factorySerialNumber]),
                },
                {
                  size: 12,
                  label: 'tm_container_labels.StartTime',
                  values: ticket.ticketInfo.startedAt
                    ? TICKET_LIST_HELPER.mapToValuesList([
                        TICKET_LIST_HELPER.formatTimeStamp(ticket.ticketInfo.startedAt),
                      ])
                    : [],
                },
                {
                  size: 12,
                  label: 'tm_container_labels.MachineType',
                  values: TICKET_LIST_HELPER.mapToValuesList([ticket.machineDescription.machineType]),
                },
                {
                  size: 12,
                  label: 'tm_container_labels.EndTime',
                  values: ticket.ticketInfo.completedAt
                    ? TICKET_LIST_HELPER.mapToValuesList([
                        TICKET_LIST_HELPER.formatTimeStamp(ticket.ticketInfo.completedAt),
                      ])
                    : [],
                },
                {
                  size: 12,
                  label: 'tm_container_labels.Category',
                  values: TICKET_LIST_HELPER.mapToValuesList([ticket.machineDescription.machineCategory]),
                },
                {
                  size: 12,
                  label: 'tm_container_labels.MaintenanceTime',
                  values:
                    ticket.ticketInfo.maintenanceTimeInMinutes !== undefined
                      ? TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.ticketInfo.maintenanceTimeInMinutes)
                      : [],
                  type: 'DURATION',
                },
                {
                  size: 12,
                  label: 'tm_container_labels.Sub-Category',
                  values: ticket.machineDescription.machineSubCategory
                    ? TICKET_LIST_HELPER.mapToValuesList([ticket.machineDescription.machineSubCategory])
                    : [],
                },
                {
                  size: 12,
                  label: 'tm_container_labels.DownTime',
                  values:
                    ticket.ticketInfo.downTimeInMinutes !== undefined
                      ? TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.ticketInfo.downTimeInMinutes)
                      : [],
                  type: 'DURATION',
                },
                {
                  size: 12,
                  label: 'Location Code',
                  values: TICKET_LIST_HELPER.mapToValuesList([ticket.ticketInfo.locationInfo.locationDisplayCode]),
                },
                {
                  size: 12,
                  label: 'tm_container_labels.Assignee',
                  values: ticket.ticketInfo.mechanicDetails
                    ? TICKET_LIST_HELPER.mapToValuesList([ticket.ticketInfo.mechanicDetails.employeeName])
                    : [],
                },
                {
                  size: 12,
                  label: 'tm_container_labels.Checklist',
                  values: ticket.ticketInfo.serviceTasks.map((checklist) => {
                    return {
                      displayName: checklist.task,
                      icon: checklist.isCompleted ? 'assets/icon/ticket-management/green-checkmark.svg' : undefined,
                    };
                  }),
                },
              ],
            },
          ],
          actions:
            selectedTicketStatus && tickets.length ? _getactionsInfoByStatus(selectedTicketStatus, actionConf) : [],
        };
      }),
    );
  },
};

export const MAINTENANCE_TICKETS_TICKET_DETAILS_MODEL = {
  createModel(
    tickets: TRESPONSE_GET_ALL_MAINTENANCE_TICKETS['maintenanceTickets'],
    actionConf: TAccordionActionInfo[],
    selectedTicketStatus?: TMaintenanceTicketStatus,
    redirectedTicketId?: number,
  ): TAccordionTableVM {
    return ACCORDION_TABLE_MODIFIER.createModel(
      [
        { label: 'tm_container_labels.ScheduledDate', colSize: 7 },
        { label: 'tm_container_labels.MachineSerialIDheader', colSize: 9 },
        { label: 'tm_container_labels.LocationHeader', colSize: 7 },
      ],
      tickets.map((ticket) => {
        return {
          id: ticket.ticketInfo.ticketId,
          tag: undefined,
          isMinimized: redirectedTicketId ? !(redirectedTicketId === ticket.ticketInfo.ticketId) : true,
          rowItems: [
            {
              type: 'TEXT',
              value: ticket.ticketInfo.dueAt,
            },
            { type: 'TEXT', value: ticket.machineDescription.factorySerialNumber },
            { type: 'TEXT', value: ticket.ticketInfo.locationInfo.locationDisplayCode },
          ],
          moreInfo: [
            {
              items: [
                {
                  size: 12,
                  label: 'tm_container_labels.MachineSerialID',
                  values: TICKET_LIST_HELPER.mapToValuesList([ticket.machineDescription.factorySerialNumber]),
                },

                {
                  size: 12,
                  label: 'tm_container_labels.StartTime',
                  values: ticket.ticketInfo.startedAt
                    ? TICKET_LIST_HELPER.mapToValuesList([
                        TICKET_LIST_HELPER.formatTimeStamp(ticket.ticketInfo.startedAt),
                      ])
                    : [],
                },
                {
                  size: 12,
                  label: 'tm_container_labels.MachineType',
                  values: TICKET_LIST_HELPER.mapToValuesList([ticket.machineDescription.machineType]),
                },
                {
                  size: 12,
                  label: 'tm_container_labels.EndTime',
                  values: ticket.ticketInfo.completedAt
                    ? TICKET_LIST_HELPER.mapToValuesList([
                        TICKET_LIST_HELPER.formatTimeStamp(ticket.ticketInfo.completedAt),
                      ])
                    : [],
                },
                {
                  size: 12,
                  label: 'tm_container_labels.Category',
                  values: TICKET_LIST_HELPER.mapToValuesList([ticket.machineDescription.machineCategory]),
                },
                {
                  size: 12,
                  label: 'tm_container_labels.MaintenanceTime',
                  values:
                    ticket.ticketInfo.maintenanceTimeInMinutes !== undefined
                      ? TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.ticketInfo.maintenanceTimeInMinutes)
                      : [],
                  type: 'DURATION',
                },
                {
                  size: 12,
                  label: 'tm_container_labels.Sub-Category',
                  values: ticket.machineDescription.machineSubCategory
                    ? TICKET_LIST_HELPER.mapToValuesList([ticket.machineDescription.machineSubCategory])
                    : [],
                },
                {
                  size: 12,
                  label: 'tm_container_labels.DownTime',
                  values:
                    ticket.ticketInfo.downTimeInMinutes !== undefined
                      ? TICKET_LIST_HELPER.timeInMinsToDisplayString(ticket.ticketInfo.downTimeInMinutes)
                      : [],
                  type: 'DURATION',
                },
                {
                  size: 12,
                  label: 'tm_container_labels.Assignee',
                  values: ticket.ticketInfo.mechanicDetails
                    ? TICKET_LIST_HELPER.mapToValuesList([ticket.ticketInfo.mechanicDetails.employeeName])
                    : [],
                },
                {
                  size: 12,
                  label: 'tm_container_labels.Checklist',
                  values: ticket.ticketInfo.serviceTasks.map((checklist) => {
                    return {
                      displayName: checklist.task,
                      icon: checklist.isCompleted ? 'assets/icon/ticket-management/green-checkmark.svg' : undefined,
                    };
                  }),
                },
              ],
            },
          ],
          actions:
            selectedTicketStatus && tickets.length ? _getactionsInfoByStatus(selectedTicketStatus, actionConf) : [],
        };
      }),
    );
  },
};
